import React, { useState } from 'react';
import { TEXTS, PENDING } from '../data/texts';
import { useVocab } from '../hooks/useVocab';

const DIALECT_NAMES = { tgdaya: 'Tgdaya 德固達雅', toda: 'Toda 都達', truku: 'Truku 太魯閣' };
const ORDER = ['tgdaya', 'toda', 'truku'];

// Cover art per story (in public/covers/).
const PUB = process.env.PUBLIC_URL || '';
// One cover for every panel — they are the same book, read in different languages.
const COVER = PUB + '/covers/bible.jpg';

// Folder (collection) art — the stories inside stay text-only.
const COLLECTION_COVERS = {
  'Temi Nawi 講述的傳說 · Tgdaya 德固達雅': PUB + '/covers/temi94_folder.jpg',
};

// 書庫 — ports the phone LibraryScreen: ONE combined list across all dialects,
// each card showing sentence count + per-dialect 學習中/已知 counts.
// Stories sharing a `collection` fold into ONE compact folder card (closed by
// default) so a big collection doesn't push the other stories off screen.
export default function TextBrowser({ onSelect }) {
  const { vocab } = useVocab();
  const [open, setOpen] = useState({});

  const counts = (dialect) => {
    let learning = 0, known = 0;
    for (const [k, c] of Object.entries(vocab)) {
      if (!k.startsWith(dialect + ':')) continue;
      if (c.status === 'learning') learning++;
      else if (c.status === 'known') known++;
    }
    return { learning, known };
  };

  const sentCount = (t) =>
    (t.paragraphs || []).reduce((n, p) => n + (p.sentences || []).length, 0);

  // A panel's chapters ARE its highlight passages.
  const chapCount = (t) => `${(t.sections || []).length} 篇`;

  // 待補: how many verses in this panel still have no recording.
  const pendingAudio = (t) => (t.paragraphs || [])
    .reduce((n, p) => n + (p.sentences || []).filter(s => s.noAudio).length, 0);

  const storyCard = ({ t, dialect }, inFolder) => {
    const c = counts(dialect);
    return (
      <button key={t.id} className={'lib-card' + (inFolder ? ' lib-card-infolder' : '')}
              onClick={() => onSelect(t)}>
        <img className="lib-cover" src={COVER} alt="" loading="lazy" />
        <div className="lib-body">
          <div className="lib-title">{t.title}</div>
          <div className="lib-sub">{t.titleTranslation} · {DIALECT_NAMES[dialect] || dialect}</div>
          <div className="lib-meta">
            {chapCount(t)}　{sentCount(t)} 節
            {pendingAudio(t) ? <span className="lib-pending">　語音待補 {pendingAudio(t)}</span> : ''}
          </div>
          <div className="lib-meta">學習中 {c.learning}　已知 {c.known}</div>
        </div>
      </button>
    );
  };

  // Group consecutive same-collection packs into folder items.
  const packs = ORDER.flatMap(d => (TEXTS[d] || []).map(t => ({ t, dialect: d })));
  const items = [];
  for (const pk of packs) {
    const col = pk.t.collection;
    const last = items[items.length - 1];
    if (col && last && last.folder === col) last.packs.push(pk);
    else if (col) items.push({ folder: col, packs: [pk] });
    else items.push({ pack: pk });
  }

  return (
    <div className="lib-scroll">
      <h2 className="lib-h">書庫</h2>
      {items.map((it) => {
        if (!it.folder) return storyCard(it.pack, false);
        const isOpen = !!open[it.folder];
        const total = it.packs.reduce((n, p) => n + sentCount(p.t), 0);
        return (
          <React.Fragment key={it.folder}>
            <button className="lib-card"
                    onClick={() => setOpen(o => ({ ...o, [it.folder]: !isOpen }))}>
              {COLLECTION_COVERS[it.folder] &&
                <img className="lib-cover" src={COLLECTION_COVERS[it.folder]} alt="" loading="lazy" />}
              <div className="lib-body">
                <div className="lib-title">{it.folder}</div>
                <div className="lib-meta">{it.packs.length} 篇 · {total} 句{isOpen ? '' : ' · 點擊展開'}</div>
              </div>
            </button>
            {isOpen && it.packs.map(pk => storyCard(pk, true))}
          </React.Fragment>
        );
      })}
      {PENDING.map(p => (
        <div key={p.id} className="lib-card lib-card-pending">
          <img className="lib-cover" src={COVER} alt="" loading="lazy" />
          <div className="lib-body">
            <div className="lib-title">{p.title}</div>
            <div className="lib-sub">{p.titleTranslation} · {DIALECT_NAMES[p.dialect]}</div>
            <div className="lib-meta">{p.note}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
